import { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { useHistory } from "react-router-dom";
import hero from "../assets/IMG_2559.JPG";

export default function Hero() {
  const [loaded, setLoaded] = useState(false);
  const navigate = useHistory();

  useEffect(() => {
    const img = new Image();
    img.src = hero;
    img.onload = () => setLoaded(true);
  }, []);

  return (
    <HeroSection id="home">
      <HeroBg>
        <HeroImg src={hero} alt="La Petite Charrue" $loaded={loaded} />
        <Overlay />
      </HeroBg>

      <HeroContent $loaded={loaded}>
        <Eyebrow>Auberge du Terroir — Territoire de Belfort</Eyebrow>

        <HeroTitle>
          La Petite Charrue
        </HeroTitle>

        <Divider>
          <span />
          <DiamondIcon>◆</DiamondIcon>
          <span />
        </Divider>

        <HeroTagline>
          <em>Comtois rends-toi, Nenni ma foi.</em>
        </HeroTagline>

        <HeroSub>
          Telle est notre devise, à nous Franc-Comtois,<br />
          depuis la nuit des temps&hellip;
        </HeroSub>

        <HeroCta>
          <CtaBtn onClick={() => navigate.push("/les-menus")}>
            Découvrir nos menus
          </CtaBtn>
          <CtaOutline
            onClick={() => {
              const el = document.getElementById("footer");
              if (el) el.scrollIntoView({ behavior: "smooth" });
            }}
          >
            Nous contacter
          </CtaOutline>
        </HeroCta>
      </HeroContent>

      <ScrollHint>
        <ScrollLine />
        <ScrollLabel>Défiler</ScrollLabel>
      </ScrollHint>
    </HeroSection>
  );
}

/* ─── Keyframes ─── */
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.6; transform: scaleY(1); }
  50%       { opacity: 1;   transform: scaleY(1.3); }
`;

const zoomIn = keyframes`
  from { transform: scale(1.05); }
  to   { transform: scale(1); }
`;

/* ─── Styled components ─── */
const HeroSection = styled.section`
  position: relative;
  height: 100dvh;
  min-height: 600px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 0 !important;
`;

const HeroBg = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
`;

const HeroImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  opacity: ${({ $loaded }) => ($loaded ? 1 : 0)};
  transition: opacity 0.6s ease;
  animation: ${zoomIn} 8s ease forwards;
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom,
    rgba(10, 6, 4, 0.35) 0%,
    rgba(10, 6, 4, 0.55) 40%,
    rgba(10, 6, 4, 0.8)  80%,
    rgba(10, 6, 4, 1)   100%
  );
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  text-align: center;
  padding: 0 1.25rem;
  max-width: 760px;
  opacity: ${({ $loaded }) => ($loaded ? 1 : 0)};
  transition: opacity 0.4s ease;

  & > * {
    animation: ${fadeUp} 0.9s ease both;
  }
  & > *:nth-child(1) { animation-delay: 0.1s; }
  & > *:nth-child(2) { animation-delay: 0.25s; }
  & > *:nth-child(3) { animation-delay: 0.4s; }
  & > *:nth-child(4) { animation-delay: 0.5s; }
  & > *:nth-child(5) { animation-delay: 0.6s; }
  & > *:nth-child(6) { animation-delay: 0.75s; }
`;

const Eyebrow = styled.p`
  font-family: var(--font-script);
  font-size: clamp(0.85rem, 2vw, 1rem);
  color: var(--primary);
  letter-spacing: 0.18em;
  text-transform: uppercase;
  margin-bottom: 1rem;
`;

const HeroTitle = styled.h1`
  font-family: var(--font-heading);
  font-size: clamp(2.8rem, 8vw, 6rem);
  font-weight: 600;
  color: var(--cream);
  letter-spacing: 0.04em;
  line-height: 1.05;
  margin-bottom: 0.75rem;
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin: 1.25rem auto;

  span {
    display: block;
    width: 80px;
    height: 1px;
    background: linear-gradient(to right, transparent, var(--primary));

    &:last-child {
      background: linear-gradient(to left, transparent, var(--primary));
    }
  }
`;

const DiamondIcon = styled.span`
  color: var(--primary);
  font-size: 0.6rem;
  line-height: 1;
`;

const HeroTagline = styled.p`
  font-family: var(--font-heading);
  font-size: clamp(1.15rem, 3vw, 1.6rem);
  color: var(--cream);
  font-style: italic;
  margin-bottom: 0.75rem;
`;

const HeroSub = styled.p`
  font-size: clamp(0.9rem, 2vw, 1.05rem);
  color: var(--cream-dim);
  line-height: 1.8;
  margin-bottom: 2.5rem;
  letter-spacing: 0.04em;
`;

const HeroCta = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const CtaBtn = styled.button`
  padding: 0.85rem 2.25rem;
  background: linear-gradient(135deg, var(--primary), var(--primary-dark));
  color: var(--bg-base);
  font-family: var(--font-body);
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    background: linear-gradient(135deg, var(--primary-light), var(--primary));
    transform: translateY(-2px);
    box-shadow: 0 8px 28px rgba(200, 151, 62, 0.35);
  }
`;

const CtaOutline = styled.button`
  padding: 0.85rem 2.25rem;
  background: transparent;
  color: var(--cream);
  font-family: var(--font-body);
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  border: 1px solid rgba(245, 237, 216, 0.45);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    border-color: var(--cream);
    background: rgba(245, 237, 216, 0.08);
    transform: translateY(-2px);
  }
`;

const ScrollHint = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;

  @media (max-width: 480px) { display: none; }
`;

const ScrollLine = styled.div`
  width: 1px;
  height: 40px;
  background: var(--primary);
  animation: ${pulse} 2s ease-in-out infinite;
`;

const ScrollLabel = styled.span`
  font-size: 0.65rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--primary);
  font-weight: 700;
`;
